"use client";

import React, { useState } from "react";
import SectionHeader from "./section-header";
import { faqs } from "@/lib/data";

/**
 * Faq Component
 * 
 * Renders the Frequently Asked Questions section as an interactive accordion block.
 * Users can expand or collapse individual questions to view their respective answers.
 * Only one FAQ is typically expanded at a time to keep the UI clean.
 * 
 * @returns {JSX.Element} The rendered FAQ section.
 */
const Faq = () => {
  /**
   * Tracks the index of the currently expanded FAQ item.
   * Defaults to 0 (the first item is open initially).
   * A value of null indicates that all items are closed.
   */
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  /**
   * Toggles the visibility of an FAQ answer.
   * If the currently clicked FAQ is already open, it closes it (sets state to null).
   * 
   * @param {number} index - The index of the FAQ item being clicked.
   */
  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };


  return (
    <section
      className="2xl:container 2xl:mx-auto 2xl:px-4 px-4"
      aria-label="FAQ Section"
      id="faq"
    >
      <div className="flex flex-col items-center max-w-[650px] xl:max-w-[712px] mx-auto space-y-4 xl:space-y-8">
        {/* Header */}
        <div className="flex flex-col items-center space-y-2.5 lg:space-y-4 xl:space-y-6 text-center">
          <SectionHeader title="FAQ" leftAlign={false} />
          {/* leading-tight */}
          <h2 className="text-[26px] lg:text-[32px] xl:text-[46px] font-semibold text-surface-black leading-tight">
            Common Questions Answered
          </h2>
        </div>

        <div className="w-full space-y-4.5 md:space-y-4 xl:space-y-6">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={faq.id}
                className="border-b border-surface/20 pb-4.5 md:pb-4 xl:pb-6"
              >
                <button
                  className="flex items-center justify-between w-full text-left focus:outline-none cursor-pointer"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={isOpen}
                >
                  <span
                    className={`text-sm xl:text-base font-medium transition-colors duration-300 ${isOpen ? "text-primary" : "text-surface-black"}`}
                  >
                    {faq.question}
                  </span>
                  <div className="text-primary shrink-0 ml-2.5 xl:ml-4">
                    <svg
                      className={`w-5 h-5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100 mt-4 xl:mt-6" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="text-sm xl:text-base text-surface/80 leading-relaxed max-w-[90%]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Faq;
